import { useCallback, useMemo } from 'react';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import theme from '@elementary/common-ui/style/theme';
import { TEST_TYPES } from '@elementary/common-ui/utils/testTypes';
import dayjs from 'dayjs';
import {
  DASHBOARD,
  DASHBOARD_STATS,
} from '@elementary/common-ui/constants/queryKeys';
import useAccountSettings from 'shared/hooks/useAccountSettings';
import useEnvironments from 'shared/hooks/useEnvironments';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import { hashArray } from '@elementary/common-ui/utils/hashing';
import { useBreadcrumbs } from '@elementary/common-ui/hooks/useBreadcrumbs';
import { FilterOption } from '@elementary/common-ui/components';
import useResourcesMetadataOptions from 'shared/hooks/useResourcesMetadataOptions';
import { useDashboardFilters } from '@elementary/common-ui/pages/Dashboard';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';
import useIsTreeViewLoading from 'shared/hooks/useIsTreeViewLoading';
import { DEFAULT_DAYS_BACK } from '@elementary/common-ui/constants';
import { FILTER_LABELS } from '@elementary/common-ui/constants/filterLabels';
import { getDashboardQuery, getDashboardStatsQuery } from './api';
import { DashboardQuery, DashboardStatsQuery, PieChartData } from './types';

const statusColorMap: Record<string, string> = {
  pass: theme.palette.common.green,
  fail: theme.palette.common.red,
  warn: theme.palette.common.orange,
  error: theme.palette.common.gray1,
  passed: theme.palette.common.green,
  failed: theme.palette.common.red,
  warning: theme.palette.common.orange,
};

const monitoredColorMap: Record<string, string> = {
  monitored: theme.palette.common.green,
  unmonitored: theme.palette.common.gray1,
};

const runStatusColorMap: Record<string, string> = {
  success: theme.palette.common.green,
  error: theme.palette.common.red,
  skipped: theme.palette.common.gray1,
  'partial success': theme.palette.common.orange,
};

const colorPieChartItems = (
  data: PieChartData | undefined,
  colorMapper: Record<string, string>,
) =>
  data?.map((item) => ({
    ...item,
    color: colorMapper[item.name.toLowerCase()],
  })) || [];

const useDashboard = () => {
  const { selectedEnv } = useEnvironments();
  const { data: accountSettings } = useAccountSettings();
  const { urlTreeNode, urlSavedView } = useTreeViewParams();
  const { selectedTreeNode, selectedTreeFolderNodes } = useSelectedTreeItem();
  const isTreeViewLoading = useIsTreeViewLoading();

  const breadcrumbs = useBreadcrumbs({
    pageTitle: 'Dashboard',
  });

  const { urlFilters } = useDashboardFilters();

  const selectedTreeNodeIds = useMemo(() => {
    if (!urlTreeNode && !urlSavedView) {
      return undefined;
    }

    if (selectedTreeFolderNodes?.length) {
      return selectedTreeFolderNodes.map((node) => node.id);
    }

    if (selectedTreeNode) {
      return [selectedTreeNode.id];
    }

    return [];
  }, [selectedTreeFolderNodes, selectedTreeNode, urlSavedView, urlTreeNode]);

  const selectedTreeNodesHash = useMemo(
    () => selectedTreeNodeIds && hashArray(selectedTreeNodeIds),
    [selectedTreeNodeIds],
  );

  const {
    data: dashboardStatsData,
    isFetching: isDashboardStatsFetching,
    isFetched: isDashboardStatsFetched,
    isLoading: isDashboardStatsLoading,
  } = useGraphqlQuery<DashboardStatsQuery>({
    queryKey: [
      DASHBOARD_STATS,
      urlFilters,
      selectedEnv?.id,
      selectedTreeNodesHash,
    ],
    query: getDashboardStatsQuery,
    variables: {
      assetIds: selectedTreeNodeIds,
      filters: urlFilters || [],
    },
    select: (val) => val?.dashboardStats,
  });

  const { data, isFetching, isFetched, error, isLoading } =
    useGraphqlQuery<DashboardQuery>({
      queryKey: [DASHBOARD, urlFilters, selectedEnv?.id, selectedTreeNodesHash],
      query: getDashboardQuery,
      variables: {
        assetIds: selectedTreeNodeIds,
        tz: accountSettings?.timezone || dayjs.tz.guess(),
        filters: urlFilters || [],
      },
      select: (val) => val?.dashboard,
    });

  const isEmpty = useMemo(
    () =>
      !isLoading &&
      !isFetching &&
      !data &&
      !isDashboardStatsLoading &&
      !isDashboardStatsFetching &&
      !dashboardStatsData,
    [
      data,
      isFetching,
      isLoading,
      isDashboardStatsLoading,
      isDashboardStatsFetching,
      dashboardStatsData,
    ],
  );

  const resultsBreakdownData = useMemo(
    () =>
      colorPieChartItems(dashboardStatsData?.resultsBreakdown, statusColorMap),
    [dashboardStatsData],
  );

  const tablesHealthData = useMemo(
    () => colorPieChartItems(dashboardStatsData?.tablesHealth, statusColorMap),
    [dashboardStatsData],
  );

  const monitoredTablesData = useMemo(
    () =>
      colorPieChartItems(
        dashboardStatsData?.monitoredTables,
        monitoredColorMap,
      ),
    [dashboardStatsData],
  );

  const summaryData = useMemo(() => {
    if (!dashboardStatsData?.summary) return {};

    const summary = dashboardStatsData.summary;
    return Object.fromEntries(
      Object.entries(summary).map(([key, value]) => {
        const testType = TEST_TYPES.find((type) => type.key === key);
        return [key, { ...value, title: testType?.title ?? value.title }];
      }),
    );
  }, [dashboardStatsData]);

  const modelsHealthData = useMemo(
    () => colorPieChartItems(data?.modelsHealth, runStatusColorMap),
    [data],
  );

  const normalizeChartData = useCallback(
    (chartData: { date: string; value: number }[]) => {
      const lastSyncedAt = selectedEnv?.lastSyncedAt;

      if (!lastSyncedAt || chartData.length === 0) {
        return chartData;
      }

      const dataByDate = new Map(
        chartData.map((item) => [item.date, item.value]),
      );
      const normalized = [];

      for (let i = DEFAULT_DAYS_BACK - 1; i >= 0; i -= 1) {
        const date = dayjs(lastSyncedAt)
          .subtract(i, 'days')
          .format('YYYY-MM-DD');
        normalized.push({
          date,
          value: dataByDate.get(date) || 0,
        });
      }

      return normalized;
    },
    [selectedEnv?.lastSyncedAt],
  );

  const normalizedUniqueTestExecData = useMemo(
    () => normalizeChartData(data?.uniqueTestExecChart || []),
    [data?.uniqueTestExecChart, normalizeChartData],
  );

  const normalizedFailedTestsChartData = useMemo(
    () => normalizeChartData(data?.failedTestsChart || []),
    [data?.failedTestsChart, normalizeChartData],
  );

  const normalizedWarningTestsChartData = useMemo(
    () => normalizeChartData(data?.warningTestsChart || []),
    [data?.warningTestsChart, normalizeChartData],
  );

  const normalizedMonitoredTablesChartData = useMemo(
    () => normalizeChartData(data?.monitoredTablesChart || []),
    [data?.monitoredTablesChart, normalizeChartData],
  );

  const normalizedTablesWithTestFailuresChartData = useMemo(
    () => normalizeChartData(data?.tablesWithTestFailuresChart || []),
    [data?.tablesWithTestFailuresChart, normalizeChartData],
  );

  const normalizedModelFailuresChartData = useMemo(
    () => normalizeChartData(data?.modelFailuresChart || []),
    [data?.modelFailuresChart, normalizeChartData],
  );

  const statusChartsDomainYDataMax = useMemo(() => {
    const failuresMax = Math.max(
      ...normalizedFailedTestsChartData.map((item) => item.value),
    );

    const warningsMax = Math.max(
      ...normalizedWarningTestsChartData.map((item) => item.value),
    );

    return Math.max(failuresMax, warningsMax);
  }, [normalizedFailedTestsChartData, normalizedWarningTestsChartData]);

  const {
    tags: tagFilterOptions,
    owners: ownerFilterOptions,
    isCritical,
    subscribers: subscriberFilterOptions,
    names: namesFilterOptions,
    databases: databaseFilterOptions,
    schemas: schemaFilterOptions,
  } = useResourcesMetadataOptions();

  const filterOptions: FilterOption[] = useMemo(
    () => [
      {
        propertyLabel: FILTER_LABELS.asset_tag,
        property: 'tag',
        options: tagFilterOptions.map((o) => ({ value: o, label: o })),
        operators: ['equals', 'notEquals'],
      },
      {
        propertyLabel: FILTER_LABELS.asset_owner,
        property: 'owner',
        options: ownerFilterOptions.map((o) => ({ value: o, label: o })),
        operators: ['equals', 'notEquals'],
      },
      {
        propertyLabel: FILTER_LABELS.asset_name,
        property: 'name',
        options: namesFilterOptions.map((o) => ({ value: o, label: o })),
        operators: ['equals', 'notEquals'],
      },

      {
        propertyLabel: FILTER_LABELS.asset_subscriber,
        property: 'subscriber',
        options: subscriberFilterOptions.map((o) => ({ value: o, label: o })),
        operators: ['equals', 'notEquals'],
      },
      {
        propertyLabel: FILTER_LABELS.asset_path,
        property: 'path',
        options: [],
        operators: ['contains', 'notContains'],
      },
      {
        propertyLabel: FILTER_LABELS.is_critical,
        property: 'is_critical',
        operators: ['equals', 'notEquals'],
        options: isCritical.map((option) => ({
          value: String(option),
          label: option ? 'True' : 'False',
        })),
      },
      {
        propertyLabel: FILTER_LABELS.database_name,
        property: 'database_name',
        options: databaseFilterOptions.map((o) => ({ value: o, label: o })),
        operators: ['equals', 'notEquals'],
      },
      {
        propertyLabel: FILTER_LABELS.schema_name,
        property: 'schema_name',
        options: schemaFilterOptions.map((o) => ({ value: o, label: o })),
        operators: ['equals', 'notEquals'],
      },
    ],
    [
      isCritical,
      namesFilterOptions,
      ownerFilterOptions,
      subscriberFilterOptions,
      tagFilterOptions,
      databaseFilterOptions,
      schemaFilterOptions,
    ],
  );

  return {
    filterOptions,
    resultsBreakdownData,
    tablesHealthData,
    monitoredTablesData,
    modelsHealthData,
    summaryData,
    uniqueTestExecData: normalizedUniqueTestExecData,
    failedTestsChartData: normalizedFailedTestsChartData,
    warningTestsChartData: normalizedWarningTestsChartData,
    monitoredTablesChartData: normalizedMonitoredTablesChartData,
    tablesWithTestFailuresChartData: normalizedTablesWithTestFailuresChartData,
    modelFailuresChartData: normalizedModelFailuresChartData,
    statusChartsDomainYDataMax,
    testTypes: TEST_TYPES,
    daysBack: DEFAULT_DAYS_BACK,
    breadcrumbs,
    isEmpty,
    isLoading: isFetching || isLoading || (isTreeViewLoading && !!urlTreeNode),
    isLoaded: isFetched,
    error,
    isDashboardStatsLoading:
      isDashboardStatsLoading || isDashboardStatsFetching,
    isDashboardStatsLoaded: isDashboardStatsFetched,
  };
};

export default useDashboard;
